/**
 * @ngdoc directive
 * @name flowHistoryStep
 * @module flowingly.runner.flow
 * @description This component is used to display information about a completed step.
 * @usage
 * ```
     <flow-history-step ng-if="step.completed" step="step" is-mobile="$ctrl.isMobile"></flow-history-step>
 * ``` 
 * ### Notes
 * See Also: https://bizflo.atlassian.net/wiki/display/TECH/Angular+Flow+Components
 * ### Properties
 * #### Inputs
 * * step: the step data to display (JSON)
 * * isMobile: show mobile view if set
 */

import { SharedAngular } from '@Client/@types/sharedAngular';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';
import { FormFieldType } from '@Shared.Angular/flowingly.services/flowingly.constants';
import angular, {
  IHttpService,
  ISCEService,
  ITimeoutService,
  IWindowService
} from 'angular';

const AvatarNames = {
  Email: 'Email',
  ExternalUser: 'External User'
} as const;

angular.module('flowingly.runner.flow').component('flowHistoryStep', {
  bindings: {
    step: '<',
    isMobile: '<', //show mobile view if set
    flow: '<'
  },
  controller: [
    '$sce',
    '$http',
    'sessionService',
    '$timeout',
    '$window',
    'pubsubService',
    'fileService',
    'browserUtilsService',
    'momentService',
    function (
      $sce: ISCEService,
      $http: IHttpService,
      sessionService: SharedAngular.SessionService,
      $timeout: ITimeoutService,
      $window: IWindowService,
      pubsubService: SharedAngular.PubSubService,
      fileService: SharedAngular.FileService,
      browserUtilsService: SharedAngular.BrowserUtilsService,
      moment: Moment
    ) {
      const $ctrl = this;
      $ctrl.$sce = $sce;
      $ctrl.isMobileApp = browserUtilsService.isCordovaApp();
      $ctrl.completedApprovals = $ctrl.step.CompletedApprovals;
      $ctrl.StepStatus = getStepStatus();
      $ctrl.numberOfCompletedApprovals = $ctrl.completedApprovals.length;
      $ctrl.activityTaskType = ActivityTaskType;
      $ctrl.StepWebhooks = undefined;
      $ctrl.StepTasks = undefined;
      $ctrl.CreatedDateFormatted = moment($ctrl.step.CreatedDate).format(
        'dddd, MMMM D, YYYY'
      );

      const subscriberId = 'flowHistoryStep';
      $ctrl.displayStepWebhooks = () => {
        return $ctrl.StepWebhooks != null && $ctrl.StepWebhooks.length > 0;
      };

      $ctrl.displayStepTasks = () => {
        return $ctrl.StepTasks != null && $ctrl.StepTasks.length > 0;
      };

      $ctrl.$onInit = function () {
        $timeout(() => {
          fileService.addFileDownloadClickEventListener();
        }, 500); // Adding execution delay avoids clashing with the instruction field calling the same
        // function and in adding duplicate event listeners.

        pubsubService.subscribe(
          'SIGNALR_RUNNER_STEP_WEBHOOK_UPDATED',
          (event, data: string) => {
            // Update the status of the webhook. E.g Pending to Success.
            if (data != null && $ctrl.StepWebhooks != null) {
              const json = JSON.parse(data);
              if (
                json != null &&
                json.stepId != null &&
                json.webhookId != null
              ) {
                for (let i = 0; i < $ctrl.StepWebhooks.length; i++) {
                  const hook = $ctrl.StepWebhooks[i];
                  if (hook && hook.Id === json.webhookId) {
                    $ctrl.StepWebhooks[i] = {
                      Status: json.status,
                      Id: json.webhookId,
                      Name: hook.Name
                    };
                    break;
                  }
                }
              }
            }
          },
          subscriberId
        );

        $ctrl.$onDestroy = function () {
          pubsubService.unsubscribeAll(subscriberId);
        };

        $ctrl.StepWebhooks = $ctrl.step.Webhooks;

        // Sort the step tasks by their completed date.
        const tasks = $ctrl.step.StepTasks;
        if (tasks != null) {
          tasks.sort(function (a, b) {
            return (
              (new Date(b.CompletedDateUtc) as any) -
              (new Date(a.CompletedDateUtc) as any)
            );
          });
          $ctrl.StepTasks = tasks;
        }

        $ctrl.hasEmailRecipients = function () {
          const recipients =
            $ctrl.step.NodeCustomMail?.SelectedEmailRecipients || [];
          return recipients.some(
            (r) => r.DisplayName && r.DisplayName.trim() !== ''
          );
        };
        $ctrl.getAvatarName = function () {
          switch ($ctrl.step.StepType) {
            case $ctrl.activityTaskType.CUSTOM_EMAIL:
              return AvatarNames.Email;
            case $ctrl.activityTaskType.EXTERNAL_USER_TASK:
              return AvatarNames.ExternalUser;
            default:
              return $ctrl.step.CompletedByName;
          }
        };

        $ctrl.getStepStatusText = function () {
          if ($ctrl.isExternalUserStep() && !$ctrl.step.CompletedDateMoment) {
            if (!$ctrl.hasEmailRecipients()) {
              return 'Waiting for email to be delivered to External User';
            }
            return 'Waiting for input by External User';
          }
          return `${$ctrl.StepStatus} ${$ctrl.step.CompletedDateMoment} by ${$ctrl.step.WhoCompletedStep}`;
        };
      };

      $ctrl.shouldShowExternalUserEmailSection = function () {
        if (!$ctrl.isExternalUserStep()) {
          return false;
        }

        if ($ctrl.step.NodeCustomMail !== null) {
          return true;
        }
        return !$ctrl.step.CompletedDateMoment;
      };

      $ctrl.shouldShowStepFields = function () {
        if (!$ctrl.isExternalUserStep()) {
          return true;
        }

        return !!$ctrl.step.CompletedDateMoment;
      };

      $ctrl.isExternalUserStep = function () {
        return (
          $ctrl.step.StepType === $ctrl.activityTaskType.EXTERNAL_USER_TASK
        );
      };

      $ctrl.showResendEmailModal = false;
      $ctrl.openResendEmailDialog = function () {
        $ctrl.showResendEmailModal = true;
      };

      $ctrl.closeResendEmailModal = function (result?: {
        SelectedEmailRecipients: unknown[];
      }) {
        if (result && result.SelectedEmailRecipients) {
          $ctrl.step.NodeCustomMail.SelectedEmailRecipients =
            result.SelectedEmailRecipients;
        }
        $ctrl.showResendEmailModal = false;
      };

      $ctrl.useFlowHistoryFieldValueDirective = (type) => {
        if (
          type !== FormFieldType.FILE_UPLOAD &&
          type !== FormFieldType.TABLE &&
          type !== FormFieldType.INSTRUCTION &&
          type !== FormFieldType.MULTISELECT_LIST &&
          type !== FormFieldType.RADIO_BUTTON_LIST &&
          type !== FormFieldType.TASK_LIST &&
          type !== FormFieldType.CHECKBOX &&
          type !== FormFieldType.SIGNATURE &&
          type !== FormFieldType.ATTACH_DOCUMENT &&
          type !== FormFieldType.IMAGE &&
          type !== FormFieldType.ADDRESS &&
          type !== FormFieldType.EXTERNAL_BUSINESS &&
          
          // TECH DEBT: flows.formatter.ts converts backend FormFieldTypeEnum to mismatched strings
          // (e.g., 'textArea' not 'textarea', 'approvecomment' not 'approvalcomment').
          // These hardcoded values match the actual runtime output. See flows.formatter.ts:426-500
          type !== 'textArea' &&
          type !== 'dropdown' &&
          type !== 'approvecomment'
        ) {
          return true;
        } else {
          return false;
        }
      };

      function getStepStatus() {
        switch ($ctrl.step.Status) {
          case 0:
            return 'ToDo';
          case 1:
            return 'Hold';
          case 2:
            return 'Completed';
          case 3:
            return 'Abandoned';
          default:
        }
      }

      $ctrl.isTableHasFileUpload = function (x) {
        if (x.indexOf('"type":4') > -1) return true;
      };

      /*When integrating webhooks from Power Automate, they are added to the existing 
      node rather than being published as a new flow model, as is the case when adding webhooks through Flowingly. 
      This condition is implemented to prevent misleading webhook status messages for webhooks added from Power Automate.*/
      $ctrl.shouldShowWebhookStatus = function () {
        const powerAutomateWebhooksWithNullStatus = $ctrl.StepWebhooks.filter(
          function (hook) {
            return hook.Type === 1 && hook.Status === null;
          }
        );
        return !(
          powerAutomateWebhooksWithNullStatus.length ===
          $ctrl.StepWebhooks.length
        );
      };

      $ctrl.isWebhookQueued = function (hook) {
        if (hook.Type === 0 && hook.Status == null) {
          return true;
        }
        return false;
      };

      $ctrl.isWebhookFailed = function (hook) {
        if (hook.Status === 1 || hook.Status === 2 || hook.Status === 3) {
          return true;
        }
        return false;
      };
    }
  ],
  templateUrl:
    'Client/runner.flow/runner.flow.history/runner.flow.history.step.component.tmpl.html'
});
